import { ScheduleCustomerForDeletion } from '../../settings/models/schedule-customer-for-deletion.model';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { LoadingAction } from '../../shared/models/loading-action.enum';
import { AccountStatus, Customer, OmegaPersonalData } from '../models/customer.model';
import { LockStatus } from '../models/lock-status.enum';
import { Settings } from '../models/settings.model';
import { VerifyPhoneNumber } from '../models/update-phone-number.model';
import { getCustomEvent } from '../utils/http-request-utils';
import { AnalyticsEvent, AnalyticsService } from './analytics-service';
import { ApiService } from './api.service';
import { Store } from './store.service';
import {
  KontomatikResponse,
  QrCode,
  VerificationStatus,
  VideoVerificationSession,
} from '../../account/models/verification';
import { Consent, Regulation } from '../../shared/models/regulation.model';
import { PersonalData } from '../../shared/models/personal-data';
import { ContentDataService } from '../../liabilities/services/content-data.service';

@Injectable({
  providedIn: 'root',
})
export class CustomerStoreService extends Store<Customer> {
  private NAMESPACE = 'customer';

  constructor(private api: ApiService,
              private analytics: AnalyticsService,
              private contentDataService: ContentDataService) {
    super(null);
  }

  public loadCustomer(customerId: string): Observable<Customer> {
    return this.api.get<Customer>(`/customers/${customerId}`)
      .pipe(tap((customer) => this.setState(customer)));
  }



  public findCustomer(customerId: string): Observable<Customer> {
    return this.api.get<Customer>(`/customers/${customerId}`);
  }

  public loadPersonalDataDetails(customerId: string): Observable<Customer> {
    return this.api.get<OmegaPersonalData>(`/customers/${customerId}/details`)
      .pipe(
        tap((fullName: OmegaPersonalData) => {
          if (this.state !== null) {
            this.state.forename = fullName.firstname;
            this.state.surname = fullName.lastname;
            this.state.details = <OmegaPersonalData>fullName;

            this.state.customerBillersCount= fullName.customerBillersCount !== null ? fullName.customerBillersCount : 0;
            this.state.defermentsCount= fullName.defermentsCount !== null ? fullName.defermentsCount : 0;
            this.state.defermentsAmount=  fullName.defermentsAmount !== null ? fullName.defermentsAmount : 0;
          }
        }),
        map(() => this.state),
      );
  }

  public updateLimits(customerId: string): Observable<Customer> {
    return this.api.post<Customer>(`/customers/${customerId}/update-limits`)
      .pipe(tap((customer) => {
        this.state.paykaFinancialData = customer.paykaFinancialData;
        this.state.defermentsCount = customer.defermentsCount;
        this.state.defermentsAmount = customer.defermentsAmount;

        if (this.state.paykaFinancialData && this.state.paykaFinancialData.numberOfOutstanding) {
          this.contentDataService.refreshDefermentsState(this.state.paykaFinancialData.numberOfOutstanding);
        }
        this.setState(this.state);
      }));
  }

  public getLimits() {
    return this.updateLimits(this.state.customerId)
      .pipe(
        take(1));
  }

  public clear(): void {
    this.setState(null);
  }

  public updateSettings(settings: Settings): Observable<any> {
    return this.api.put('/customers/' + this.state.customerId + '/settings', settings).pipe(
      tap(() => {
        this.setState({ ...this.state, settings });
        this.trackCustomerUpdated();
      }),
    );
  }

  public updateEmailSettings(canSendEmail: boolean): Observable<any> {
    return this.api.put('/customers/' + this.state.customerId + '/settings/can-send-email',
      { canSendEmail: canSendEmail })
      .pipe(
        tap(() => {
          this.state.settings.liabilitiesNew.canSendEmail = canSendEmail;
          this.state.settings.canSendEmail = canSendEmail;
          this.trackCustomerUpdated();
        }),
      );
  }

  public updateRegulationSettings(id: number, checked: boolean): Observable<any> {
    return this.api.put('/customers/' + this.state.customerId + '/settings/regulation',
      {
        id: id,
        checked: checked,
      })
      .pipe(
        tap(() => {
          this.state.settings.consentsLastUpdate = Date.now();
          this.setState(this.state);
          this.trackCustomerUpdated();
        }),
      );
  }

  public updateRegulationsSettings(consents: Consent[]): Observable<any> {
    return this.api.put('/customers/' + this.state.customerId + '/settings/regulations',
      {
        consents: consents,
      })
      .pipe(
        tap(() => {
          this.state.settings.consentsLastUpdate = Date.now();
          this.setState(this.state);
          this.trackCustomerUpdated();
        }),
      );
  }

  public scheduleForDeletion(scheduleCustomerForDeletion: ScheduleCustomerForDeletion): Observable<Customer> {
    const param = getCustomEvent(LoadingAction.SCHEDULE_CUSTOMER_FOR_DELETION, this.NAMESPACE, '');

    return this.api
      .post(`/customers/${this.state.customerId}/scheduled-for-deletion`, scheduleCustomerForDeletion, { params: param })
      .pipe(switchMap(() => this.loadCustomer(this.state.customerId)));
  }

  public deleteAccount(): Observable<Customer> {
    return this.api
      .delete(`/customers/${this.state.customerId}`);
  }

  public cancelSchedulingForDeletion(): Observable<Customer> {
    return this.api.post(`/customers/${this.state.customerId}/cancel-scheduled-for-deletion`)
      .pipe(switchMap(() => this.loadCustomer(this.state.customerId)));
  }

  public verifyPhoneNumber(command: VerifyPhoneNumber): Observable<any> {
    const param = getCustomEvent(LoadingAction.PHONE_VERIFICATION, 'phone_verification', this.state.customerId);
    return this.api.put(`/customers/${this.state.customerId}/phone-number`, command, param).pipe(
      tap(() => {
        this.trackCustomerUpdated();
        this.analytics.trackEvent(new AnalyticsEvent('Customer verified'));
      }),
      switchMap(() => this.loadCustomer(this.state.customerId)),
    );
  }

  public getInboxEmailAddress(): Observable<any> {
    return this.api.get<{ inboxEmailAddress: string }>(`/customers/${this.state.customerId}/inboxEmailAddress`);
  }

  public isCustomerLocked(): boolean {
    return LockStatus.BLOCKED === this.state.lockStatus || LockStatus.HARD_BLOCKED === this.state.lockStatus;
  }

  public hasCustomerBlockedStatus(): boolean {
    return LockStatus.BLOCKED === this.state.lockStatus;
  }

  public hasCustomerHardBlockedStatus(): boolean {
    return LockStatus.HARD_BLOCKED === this.state.lockStatus;
  }

  private trackCustomerUpdated(): void {
    this.analytics.trackEvent(new AnalyticsEvent('Customerupdate'));
  }

  public hasConfiguredAutomaticPayments(): boolean {
    return this.state.hasConfiguredAutomaticPayments;
  }

  /**
   * Upgrading customer to MEDIUM.
   */
  public upgradeAccountStatus(): Observable<any> {
    return this.api.post<any>('/customers/upgradeAccountStatus');
  }

  public refresh() {
    this.loadCustomer(this.state.customerId).subscribe();
  }


  public updateNick(nick: string): Observable<any> {
    let body = {
      nick: nick,
    };
    return this.api.post('/customers/' + this.state.customerId + '/nick', body).pipe(
      tap(() => {
        this.state.nick = nick;
        this.setState(this.state);
        this.trackCustomerUpdated();
      }),
    );
  }

  public updateIban(iban: string): Observable<any> {
    let body = {
      bban: iban,
    };
    return this.api.post('/customers/' + this.state.customerId + '/iban', body).pipe(
      tap(() => {
        this.state.details.returnNrb = iban;
        this.setState(this.state);
        this.trackCustomerUpdated();
      }),
    );
  }

  public getAccountStatus(): Observable<AccountStatus> {
    if (this.state === null || this.state.customerId === null) {
      return of(null);
    } else {
      if (this.state.accountStatus === AccountStatus.MEDIUM) {
        return of(AccountStatus.MEDIUM);
      }
    }

    return this.api.get<AccountStatus>(`/customers/${this.state.customerId}/status`)
      .pipe(tap((status) => {
        let customer = this.state;
        customer.accountStatus = status;
        this.setState(customer);
        return status;
      }));
  }

  public getMObywatelQrCode(): Observable<QrCode> {
    return this.api.get<QrCode>(`/customers/${this.state.customerId}/mobywatel/qrcode`, {})
      .pipe(map(value => {
        value.expirationDate = new Date(value.expirationDate);
        return value;
      }));
  }


  public getMarketingRegulations(): Observable<Regulation[]> {
    return this.api.get<Regulation[]>(`/customers/${this.state.customerId}/regulations/marketing`, {
      observe: 'body',
    });
  }

  /**
   *
   * @param kontomatikResponse - The response from the Kontomatik.
   * @param personalData - personal data to confirmation.
   */
  upgradeAccountWithKontomatik(kontomatikResponse: KontomatikResponse, personalData: PersonalData)
    : Observable<VerificationStatus> {
    return this.api.post<VerificationStatus>('/customers/upgrade-account-status/with-kontomatik', {
      kontomatik: kontomatikResponse,
      personalData: personalData,
    });
  }

  /**
   //    * Subscribing information about verification account from websocket server.
   //    */
  subscribeMObywatelVerification(): Observable<QrCode> {
    return this.getMObywatelQrCode();
  }

  public hideBannerInfo(bannerId: string): Observable<any> {
    return this.api.delete(`/customers/banners/${bannerId}`);
  }

  startVideoVerification()
    : Observable<VideoVerificationSession> {
    return this.api.get<VideoVerificationSession>('/customers/upgrade-account-status/video-verification/session');
  }

  checkVideoVerification(id: string)
    : Observable<Boolean> {
    return this.api.get<Boolean>('/customers/upgrade-account-status/video-verification/session/' + id);
  }

  upgradeUsingVideoVerification(id: string, personalData: PersonalData)
    : Observable<VerificationStatus> {
    return this.api.post<VerificationStatus>('/customers/upgrade-account-status/video-verification/session/' + id + '/upgrade',
        personalData.address
      );
  }

}
